import { useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useVerifyEmail, useResendVerification } from '@/hooks/use-auth-api'
import toast from 'react-hot-toast'

type VerificationStatus = 'loading' | 'success' | 'error' | 'no-token'

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const verifyEmailMutation = useVerifyEmail()
  const resendMutation = useResendVerification()
  const [status, setStatus] = useState<VerificationStatus>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [resendSent, setResendSent] = useState(false)

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setStatus('no-token')
      return
    }

    const verifyEmail = async () => {
      try {
        await verifyEmailMutation.mutateAsync(token)
        setStatus('success')
        toast.success('Email verified successfully!')
      } catch (error: any) {
        setStatus('error')
        setErrorMessage(error.message || 'Email verification failed')
        toast.error(error.message || 'Email verification failed')
      }
    }

    verifyEmail()
  }, [token])

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address')
      return
    }

    try {
      await resendMutation.mutateAsync(email)
      setResendSent(true)
      toast.success('Verification email sent!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend verification email')
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Verifying your email...
            </CardTitle>
            <CardDescription>
              Please wait while we verify your email address.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Email verified!
            </CardTitle>
            <CardDescription>
              Your email has been successfully verified. You can now access all features of your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Go to Dashboard
            </Button>
            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Or sign in to continue
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Verification failed
            </CardTitle>
            <CardDescription>
              {errorMessage || 'The verification link is invalid or has expired.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!resendSent ? (
              <>
                <p className="text-sm text-muted-foreground text-center">
                  Enter your email to receive a new verification link:
                </p>
                <div className="space-y-2">
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
                <Button
                  onClick={handleResendVerification}
                  className="w-full"
                  loading={resendMutation.isPending}
                >
                  Resend Verification Email
                </Button>
              </>
            ) : (
              <div className="text-center text-sm text-muted-foreground">
                <p>We've sent a new verification email to <strong>{email}</strong>.</p>
                <p className="mt-2">Please check your inbox and spam folder.</p>
              </div>
            )}
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-primary hover:underline"
              >
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // no-token state
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/20">
            <XCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
          </div>
          <CardTitle className="text-2xl font-bold">
            Missing verification token
          </CardTitle>
          <CardDescription>
            No verification token was provided. Please use the link from your email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!resendSent ? (
            <>
              <p className="text-sm text-muted-foreground text-center">
                Enter your email to receive a new verification link:
              </p>
              <div className="space-y-2">
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
              <Button
                onClick={handleResendVerification}
                className="w-full"
                loading={resendMutation.isPending}
              >
                Resend Verification Email
              </Button>
            </>
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              <p>We've sent a verification email to <strong>{email}</strong>.</p>
              <p className="mt-2">Please check your inbox and spam folder.</p>
            </div>
          )}
          <div className="text-center">
            <Link
              to="/login"
              className="text-sm text-primary hover:underline"
            >
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
